import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RoutePath, routeAccess } from 'src/router';

import { useSelector } from 'src/store';
import {
  isLoggedInSelector,
  authUserIdSelector,
  authRoleSelector,
  authRoleListSelector,
} from 'src/store/selectors/authSelector';
import {
  tenantBrandingLogoUrlSelector,
  tenantBrandingLogoAltSelector,
  tenantBrandingNameSelector,
} from 'src/store/selectors/tenantBrandingSelector';
import { selectedCompanyIdSelector } from 'src/store/selectors/companySelector';

import { LinkWithParams, Navigation, NavigationLocalConfig } from '@itm/shared-frontend/lib/components';
import { MenuIcon, FlexPortalIcon, HideIcon } from '@itm/shared-frontend/lib/components/icons';

import CompanyFilter from 'src/components/CompanyFilter';

import { clientPortalDomain, clientPortalDashboardUrl, isAzureTargetPlatform } from 'src/utils/constants';

import { getUserPermissionsByCompany } from 'src/api/clientPortal/permissions';

import { Role, GlobalRole, UserPermissionResponse } from 'src/types';

import styles from './Sidebar.module.scss';

type Props = Readonly<{
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}>;

function Sidebar({ isActive, setIsActive }: Props) {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userId = useSelector(authUserIdSelector);
  const userRole = useSelector(authRoleSelector);
  const userRoleListFromToken = useSelector(authRoleListSelector);
  const brandingLogoUrl = useSelector(tenantBrandingLogoUrlSelector);
  const brandingLogoAlt = useSelector(tenantBrandingLogoAltSelector);
  const brandingName = useSelector(tenantBrandingNameSelector);

  const selectedCompanyId = useSelector(selectedCompanyIdSelector);

  const [userPermissionList, setUserPermissionList] = useState<UserPermissionResponse[]>([]);

  const userRoleList = useMemo<GlobalRole[]>(() => {
    if (!userRole) return [];
    if (userRole === Role.SuperAdmin || userRole === Role.Support) return [userRole] as unknown as GlobalRole[];

    const clientPortalRole = userRoleListFromToken.find((role) => role.startsWith('ClientPortal'));
    const productRolesByCompany = [...new Set(userPermissionList.map(({ productRoleName }) => productRoleName))];

    return clientPortalRole ? [clientPortalRole, ...productRolesByCompany] : productRolesByCompany;
  }, [userPermissionList, userRole, userRoleListFromToken]);

  const fetchUserPermissionList = useCallback(async () => {
    if (!userId || !selectedCompanyId || userRole === Role.SuperAdmin || userRole === Role.Support) {
      setUserPermissionList([]);
      return;
    }
    try {
      const res = await getUserPermissionsByCompany(userId, selectedCompanyId);
      setUserPermissionList(res.data);
    } catch {
      console.warn('User Permissions unavailable');
    }
  }, [userId, selectedCompanyId, userRole]);

  const navigationLocalConfig = useMemo<NavigationLocalConfig<Role>>(
    () => [
      {
        name: 'All Products',
        section: 'products',
        path: clientPortalDashboardUrl,
        icon: () => <MenuIcon />,
      },
      {
        name: 'Flex Portal',
        section: 'products',
        path: RoutePath.root,
        isInternalRoute: true,
        icon: () => <FlexPortalIcon />,
        children: [
          {
            name: 'Configuration',
            path: RoutePath.configurationRoot,
            role: { allowList: routeAccess.configurationRoot },
            children: [
              {
                name: 'General settings',
                path: RoutePath.configurationGeneralSettingsRoot,
                role: { allowList: routeAccess.configurationGeneralSettingsRoot },
              },
              {
                name: 'Scheme Configuration',
                path: RoutePath.configurationSchemeRoot,
                role: { allowList: routeAccess.configurationSchemeRoot },
              },
              {
                name: 'Employer configuration',
                path: RoutePath.configurationEmployerRoot,
              },
            ],
          },
          {
            name: 'Data Management',
            path: RoutePath.dataManagementRoot,
            role: { allowList: routeAccess.dataManagementRoot },
            children: [
              {
                name: 'New Monthly Process',
                path: RoutePath.dataManagementProcessCreateRoot,
              },
              {
                name: 'Monthly Process summary',
                path: RoutePath.dataManagementProcessSummary,
              },
            ],
          },
          {
            name: 'Reporting',
            path: RoutePath.reportingRoot,
            role: { allowList: routeAccess.reportingRoot },
            children: [
              {
                name: 'Invoice History',
                path: RoutePath.reportingInvoiceHistoryRoot,
              },
              {
                name: 'All Templates',
                path: RoutePath.reportingTemplatesRoot,
                role: { allowList: routeAccess.reportingTemplatesRoot },
              },
            ],
          },
          {
            name: 'Product Information',
            path: RoutePath.productInfo,
            role: { allowList: routeAccess.productInfo },
          },
        ],
      },
    ],
    [],
  );

  useEffect(() => {
    fetchUserPermissionList();
  }, [fetchUserPermissionList]);

  return (
    <aside
      className={classNames([
        styles.Wrapper,
        'is-flex is-flex-direction-column has-background-white is-relative',
        { [styles.IsActive]: isActive },
      ])}
      id="sidebar"
    >
      <header className={classNames(styles.Header, 'is-flex-shrink-0')}>
        <div className="columns is-align-items-center is-gapless mb-0">
          <div className="column is-narrow">
            <LinkWithParams className={classNames(styles.Logo, 'has-icon')} to={RoutePath.root}>
              <img src={brandingLogoUrl} alt={brandingLogoAlt} />
            </LinkWithParams>
          </div>
          {isActive && (
            <div className="column">
              <h2 className="title is-nowrap is-4 ml-4">{brandingName}</h2>
            </div>
          )}
        </div>
        {isActive && <CompanyFilter />}
      </header>
      {isLoggedIn && (
        <>
          <button
            className={classNames(styles.ToggleButton, 'has-icon', { 'is-active': isActive })}
            type="button"
            onClick={() => setIsActive(!isActive)}
            aria-controls="sidebar"
            aria-label={isActive ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className="icon">
              <HideIcon />
            </span>
          </button>
          {userRole && (
            <Navigation
              isActiveSidebar={isActive}
              clientPortalDomain={clientPortalDomain}
              localConfig={navigationLocalConfig}
              userRoleList={userRoleList}
              isAzureTargetPlatform={isAzureTargetPlatform}
              isCommonItemsCollapse
            />
          )}
        </>
      )}
    </aside>
  );
}

export default Sidebar;
